import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AuthState, AuthUser } from './types';
import {
  getCurrentUser,
  initializeSession,
  login as loginRequest,
  logout as logoutRequest,
  subscribeToSession,
} from './authService';
import { clearAllMenuCaches, clearCacheOnLogout } from '../Common/menuCacheUtils';
import { clearWalkthroughSessionKeys } from '../hooks/usePageWalkthrough';

interface AuthContextValue extends AuthState {
  login: (
    email: string,
    password: string,
    ipAddress?: string,
    userAgent?: string,
    inviteToken?: string
  ) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(getCurrentUser());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    initializeSession()
      .then((restoredUser) => {
        if (isMounted) {
          setUser(restoredUser);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    const unsubscribe = subscribeToSession((sessionUser) => {
      setUser(sessionUser);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const login = useCallback(
    async (
      email: string,
      password: string,
      ipAddress?: string,
      userAgent?: string,
      inviteToken?: string
    ) => {
      clearAllMenuCaches();

      const authenticatedUser = await loginRequest(
        email,
        password,
        ipAddress,
        userAgent,
        inviteToken
      );
      clearWalkthroughSessionKeys();
      clearAllMenuCaches();
      setUser(authenticatedUser);
      return authenticatedUser;
    },
    []
  );

  const logout = useCallback(async () => {
    await logoutRequest();
    clearWalkthroughSessionKeys();
    setUser(null);
    clearCacheOnLogout();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      logout,
    }),
    [user, isLoading, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
